import React from 'react';
import { useIntl } from 'react-intl';
import { YouTubeConnectionGap } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../components/primitives/SMLButton';
import { youtubeConnectionMessages as messages } from './youtubeConnectionMessages';

interface YouTubeConnectionNoteProps {
  /** What the one YouTube sign-in lacks. Null or absent: the card shows only the scope line. */
  gap: YouTubeConnectionGap | null | undefined;
  /** Shows the Reconnect button. A card that already shows its own Connect button passes false. */
  showReconnect: boolean;
  disabled?: boolean;
  onReconnect: () => void;
  /** The note style of the card that draws it. */
  noteClassName?: string;
  /** The gap note style of the card (a soft or a warn note). */
  gapClassName?: string;
  buttonClassName?: string;
}

/**
 * The YouTube part of the Channels card and of the Catalog card: one line that says that the
 * connection is for posting and analytics, and, for a sign-in from before that lacks a scope,
 * "Reconnect to add analytics" or "Reconnect to add posting". The reconnect asks for every scope.
 * No new design: the line uses the note and button styles of each card.
 */
const YouTubeConnectionNoteComponent: React.FC<YouTubeConnectionNoteProps> = ({
  gap,
  showReconnect,
  disabled = false,
  onReconnect,
  noteClassName,
  gapClassName,
  buttonClassName,
}) => {
  const intl = useIntl();
  const hasGap = gap === 'analytics' || gap === 'posting';
  return (
    <>
      <div className={noteClassName}>{intl.formatMessage(messages.scope)}</div>
      {hasGap ? (
        <div className={gapClassName} data-testid="youtube-connection-gap">
          {intl.formatMessage(gap === 'analytics' ? messages.addAnalytics : messages.addPosting)}
        </div>
      ) : null}
      {hasGap && showReconnect ? (
        <div>
          <SMLButton variant="primary" className={buttonClassName} disabled={disabled} onClick={onReconnect}>
            {intl.formatMessage(messages.reconnect)}
          </SMLButton>
        </div>
      ) : null}
    </>
  );
};

export const YouTubeConnectionNote = TypedMemo(YouTubeConnectionNoteComponent);
